/**
 * Carousel
 * 
 * @Author: Focci
 * @Date: 2023-08-07 10:14:34
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-07 10:14:34
 */

'use client'

import PropTypes from 'prop-types'
import useProgressRouter from '@hook/useProgressRouter'
import useApp from '@hook/useApp'
import useTracking from '@hook/useTracking'
import { houseToGta, isAU } from '@lib/utils'
import { PropertyStatusMaks } from '@comp/page/common/card/property/StatusMask'
import { propertyDetailImages, propertyDetailByIdImages } from '@lib/route/house'
import { useLocale, useTranslations } from 'next-intl'
import { useState, useCallback, useRef } from 'react'
import { houseSearchType } from '@lib/config'
import PropertyCarouseTab from './PropertyCarouseTab'
import EstimateSoldCover from './estimate-sold-cover'
import CoverBody from './CoverBody'
import { MaxWidthMap } from './Map'
import { getCoverOptions, getInitImages } from './lib'
import LocationMapDialog from '../location/LocationMapDialog'

export { EstimateSoldCover }

function CarouselImage({ house, toUrl }) {
  const ts = useTranslations('search')
  const th = useTranslations('house')
  const [fired, setFired] = useState(false)
  const { pushTrack } = useTracking()
  const { options } = getCoverOptions({ house, ts, th })

  if(isAU && !fired) {
    setFired(true)
    options.forEach(() => {
      pushTrack('homely_photo_view', houseToGta(house))
    })
  }

  return (
    <CoverBody 
      house={house} 
      onClick={(...args) => toUrl(...args)} 
    />
  )
}

export default function Carousel({
  house = {},
  isSoldOrEstimate,
}) {
  const locale = useLocale()
  const th = useTranslations('house')
  const { pushTrackViewImage } = useTracking()
  const { routerPush } = useProgressRouter()
  const { isMobile } = useApp()
  const { images } = getInitImages(house, th)
  const mapDialogRef = useRef()

  const [selectedValue, setSelectedValue] = useState(
    images.length > 0 ? 'img' : (house?.lng && house?.lat ? 'map' : null)
  )

  // 跳转到图片预览
  const toUrl = useCallback((type, index) => {
    const { slug, status, id, propertyId, type: htype } = house
    const query = [`?type=${type}`]
    const url = (status === 1 || isSoldOrEstimate)
      ? propertyDetailImages(slug, locale)
      : propertyDetailByIdImages(id, locale) 
    
    index !== undefined && query.push(`index=${index}`)

    // 售出传递 propertyId给url
    if(htype === houseSearchType.sold && propertyId) {
      query.push(`pid=${propertyId}`)
    }

    // 添加浏览图片统计
    pushTrackViewImage(house)
    
    routerPush(url + query.join('&'))
  }, [house, isSoldOrEstimate, pushTrackViewImage, routerPush, locale])
  
  // Tab change
  const handleTabChange = useCallback((tab) => {
    if(tab.disabled) return
    
    if(['img', 'map'].indexOf(tab.value) > -1) {
      if(isMobile) {
        if(tab.value === 'img') {
          toUrl('all', 0)
        } else {
          mapDialogRef.current.openDialog()
        }
      } else if(tab.value === selectedValue && tab.value === 'img') {
        toUrl('all', 0)
      } else {
        setSelectedValue(tab.value)
      }
    } else {
      toUrl(tab.value, tab.index)
    }
  }, [toUrl, isMobile, selectedValue])

  // 如果没有图片或者地图，则这里不展示
  if(!selectedValue) {
    return null
  }
  
  return (
    <div className="h-[14rem] md:h-[32rem] relative">
      <div className="w-full h-full absolute inset-0">
        {selectedValue === 'img' && <CarouselImage house={house} toUrl={toUrl} />}
        {selectedValue === 'map' && <MaxWidthMap house={house} />}
        <PropertyCarouseTab 
          // value={selectedValue}
          house={house}
          onChange={handleTabChange}
        />
      </div>
      <LocationMapDialog
        ref={mapDialogRef}
        showTrigger={false}
        house={house} 
      />
      <PropertyStatusMaks 
        status={house.status} 
        className="text-[2rem]" 
      />
    </div>
  )
}

Carousel.propTypes = {
  house: PropTypes.object,
}
